import { useEffect } from 'react';

import { env } from '@/config/env';
import { getOperationalWeatherCoordinates } from '@/config/weather';
import { setWeatherPoint } from '@/features/map/stores/mapSlice';
import { useAppDispatch } from '@/hooks/useAppDispatch';
import { store } from '@/stores';
import {
  readWeatherOperationalPointCache,
  writeWeatherOperationalPointCache,
} from '@/utils/weatherOperationalPointCache';

function isValidCoords(lat: number, lng: number): boolean {
  return (
    Number.isFinite(lat) &&
    Number.isFinite(lng) &&
    lat >= -90 &&
    lat <= 90 &&
    lng >= -180 &&
    lng <= 180
  );
}

/**
 * Khởi tạo điểm thời tiết: env cố định → cache GPS → geolocation trình duyệt → (thẻ KPI fallback Hà Nội).
 * Không di chuyển tâm bản đồ — chỉ cập nhật `weatherPoint` cho thẻ KPI / modal dự báo.
 */
export function useWeatherOperationalMapSync(enabled: boolean) {
  const dispatch = useAppDispatch();
  const envFixedWeatherCoords = env.WEATHER_DEFAULT_LAT != null && env.WEATHER_DEFAULT_LON != null;

  useEffect(() => {
    if (!enabled) return;

    let cancelled = false;

    const applyBootstrapPoint = (lat: number, lng: number) => {
      if (cancelled || !isValidCoords(lat, lng)) return;
      if (store.getState().map.weatherPointUserPinned) return;
      dispatch(setWeatherPoint({ lat, lng }));
    };

    if (envFixedWeatherCoords) {
      const { lat, lng } = getOperationalWeatherCoordinates();
      applyBootstrapPoint(lat, lng);
      return () => {
        cancelled = true;
      };
    }

    const cached = readWeatherOperationalPointCache();
    if (cached) {
      applyBootstrapPoint(cached.lat, cached.lng);
    }

    if (typeof navigator === 'undefined' || !navigator.geolocation) {
      return () => {
        cancelled = true;
      };
    }

    navigator.geolocation.getCurrentPosition(
      (pos) => {
        if (cancelled) return;
        const lat = pos.coords.latitude;
        const lng = pos.coords.longitude;
        if (!isValidCoords(lat, lng)) return;
        writeWeatherOperationalPointCache(lat, lng);
        applyBootstrapPoint(lat, lng);
      },
      () => {
        /* Từ chối / lỗi GPS → WeatherKpiCard dùng Hà Nội */
      },
      { enableHighAccuracy: false, timeout: 15000, maximumAge: 300_000 },
    );

    return () => {
      cancelled = true;
    };
  }, [enabled, envFixedWeatherCoords, dispatch]);
}
